"use client";

import { c as _c } from "react/compiler-runtime";
import { OtpInput } from "@/components/auth/OtpInput";
import type { OtpChallenge } from "@/components/auth/useOtpChallenge";
import { Button } from "@/components/ui/Button";
import { loginPhoneCopy } from "@/components/profile/login-phone-copy";
interface OtpCodePanelProps {
  /** Says where the code went — two codes arrive one after the other. */
  intro: string;
  challenge: OtpChallenge;
  /** The secondary action: re-send, or start over when a re-send is not possible. */
  resendLabel: string;
  onResend: () => void;
  onCancel: () => void;
  footnote?: string;
}

/**
 * One code step of the profile's login-phone change: the six digits, checked
 * as soon as they are complete. Every button is `type="button"` — this sits
 * inside the profile form, whose submit would otherwise fire on a click.
 */
export function OtpCodePanel(t0) {
  const $ = _c(36);
  const {
    intro,
    challenge,
    resendLabel,
    onResend,
    onCancel,
    footnote
  } = t0;
  let t1;
  if ($[0] !== intro) {
    t1 = <p className="text-body-sm text-foreground">{intro}</p>;
    $[0] = intro;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-body-sm font-medium text-foreground-muted">{loginPhoneCopy.codeLabel}</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== challenge.busy || $[4] !== challenge.code || $[5] !== challenge.inputsRef || $[6] !== challenge.setCode) {
    t3 = <OtpInput code={challenge.code} onChange={challenge.setCode} inputsRef={challenge.inputsRef} disabled={challenge.busy} autoFocus={true} />;
    $[3] = challenge.busy;
    $[4] = challenge.code;
    $[5] = challenge.inputsRef;
    $[6] = challenge.setCode;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== challenge.error) {
    t4 = challenge.error && <p role="alert" className="text-body-sm text-error">{challenge.error}</p>;
    $[8] = challenge.error;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== challenge.busy || $[11] !== challenge.code) {
    t5 = !challenge.code.every(Boolean) || challenge.busy;
    $[10] = challenge.busy;
    $[11] = challenge.code;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== challenge.busy || $[14] !== challenge.verify || $[15] !== t5) {
    t6 = <Button type="button" size="sm" onClick={challenge.verify} loading={challenge.busy} disabled={t5}>{loginPhoneCopy.verify}</Button>;
    $[13] = challenge.busy;
    $[14] = challenge.verify;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== challenge.busy || $[18] !== onResend || $[19] !== resendLabel) {
    t7 = <Button type="button" size="sm" variant="ghost" onClick={onResend} disabled={challenge.busy}>{resendLabel}</Button>;
    $[17] = challenge.busy;
    $[18] = onResend;
    $[19] = resendLabel;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== challenge.busy || $[22] !== onCancel) {
    t8 = <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={challenge.busy}>{loginPhoneCopy.cancel}</Button>;
    $[21] = challenge.busy;
    $[22] = onCancel;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-2">{t6}{t7}{t8}</div>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== footnote) {
    t10 = footnote && <p className="text-body-sm text-foreground-muted">{footnote}</p>;
    $[28] = footnote;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t1 || $[31] !== t10 || $[32] !== t3 || $[33] !== t4 || $[34] !== t9) {
    t11 = <div className="space-y-3 rounded-xl border border-outline-variant/40 bg-surface-container-low p-4">{t1}{t2}{t3}{t4}{t9}{t10}</div>;
    $[30] = t1;
    $[31] = t10;
    $[32] = t3;
    $[33] = t4;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}